/**
 * Subscription auth recovery (Fix 3)
 *
 * GraphQL subscriptions run over a single long-lived WebSocket. The access
 * token is only attached once, when the socket connects (graphql-ws
 * `connectionParams`). If the token expires mid-subscription the server starts
 * rejecting frames with `unauthorized.user` ("user is not authorized") and the
 * socket never recovers on its own — the UI just sees a permanent error.
 *
 * This module holds the *decision* logic for recovering from that, kept pure
 * and dependency-free so it is unit-testable without a browser, Apollo, or a
 * live socket. The wiring (which calls into Apollo / graphql-ws / cookies /
 * window) lives in `index.tsx` and injects its side effects here.
 *
 * Design goals (from the incident review):
 *  - Recover automatically: refresh the token, force the socket to reconnect so
 *    active subscriptions resubscribe with a fresh token.
 *  - Don't fail fully open: a *transient* refresh failure (network / 5xx) must
 *    NOT log the user out. Only a *definitive* invalid signal (refresh 401 ->
 *    cookies already cleared) sends them to login.
 *  - Don't hammer: debounce bursts of errors, cap consecutive attempts, then
 *    cool down so we never spin in a tight refresh/reconnect loop.
 */

/** True for the "user is not authorized" / `unauthorized.user` family. */
export function isUnauthorizedUserError(args: {
  code?: unknown;
  message?: unknown;
}): boolean {
  const code = typeof args.code === "string" ? args.code : "";
  const message = typeof args.message === "string" ? args.message : "";
  return (
    code === "unauthorized.user" ||
    code === "forbidden.invalid_access" ||
    message.includes("user is not authorized") ||
    message.includes("user can't access this")
  );
}

/**
 * Scan a list of GraphQL-error-shaped objects (from CombinedGraphQLErrors,
 * CombinedProtocolErrors, etc.) for an unauthorized error. Tolerant of unknown
 * shapes so it works across Apollo's several error wrappers.
 */
export function containsUnauthorizedUser(
  items: ReadonlyArray<
    { code?: unknown; message?: unknown } | null | undefined
  >,
): boolean {
  for (const item of items) {
    if (!item) continue;
    if (isUnauthorizedUserError(item)) return true;
  }
  return false;
}

/**
 * Build the graphql-ws `connectionParams` auth object. Prefer a freshly
 * refreshed token, fall back to the existing cookie token. Crucially, if the
 * refresh threw (transient) we still send the cookie token instead of going
 * fully anonymous — that is the "don't fail open" rule applied at connect time.
 */
export function buildConnectionAuth(args: {
  refreshedToken?: string | null;
  cookieToken?: string | null;
}): { authentication: string } | Record<string, never> {
  const token = args.refreshedToken || args.cookieToken || "";
  if (!token) return {};
  return { authentication: "Bearer " + token };
}

/**
 * Classify a token-refresh GraphQL failure by the backend error code/message.
 *
 *  - `invalid`   -> the refresh token itself is no good and re-auth is required:
 *                   the backend's `refreshAccessToken` throws
 *                   `bad_request.invalid_token` (revoked / not in DB / bad
 *                   signature) or `bad_request.missing_token`, plus the
 *                   unauthorized/forbidden family for defensiveness.
 *  - `transient` -> everything else: an `internal_service` 5xx (the FOX-3654
 *                   incident: OAuth-secret fetch failing), a network blip, a
 *                   timeout, or an unrecognised code.
 *
 * Fail-safe by design: only a POSITIVE invalid signal returns `invalid`; on any
 * ambiguity we return `transient` so a backend hiccup keeps the session (retry)
 * instead of logging the user out. Genuine invalid tokens always carry one of
 * the explicit codes above, so they are still caught.
 */
export function classifyRefreshErrorCodes(
  items: ReadonlyArray<
    { code?: unknown; message?: unknown } | null | undefined
  >,
): "invalid" | "transient" {
  for (const item of items) {
    if (!item) continue;
    const code = typeof item.code === "string" ? item.code : "";
    const message = typeof item.message === "string" ? item.message : "";
    if (
      code === "bad_request.invalid_token" ||
      code === "bad_request.missing_token" ||
      code.startsWith("unauthorized") ||
      code.startsWith("forbidden") ||
      // Message-text fallbacks for when the wrapper drops `extensions`.
      message.includes("token is invalid") ||
      message.includes("refresh token is missing") ||
      isUnauthorizedUserError(item)
    ) {
      return "invalid";
    }
  }
  return "transient";
}

export type RefreshOutcome<TDiagnostic = undefined> =
  | { ok: true; diagnostic?: TDiagnostic }
  | {
      ok: false;
      reason: "invalid" | "transient";
      diagnostic?: TDiagnostic;
    };

export type RecoveryTrigger =
  | { kind: "subscription_graphql" }
  | { kind: "subscription_protocol" }
  | { kind: "ws_close"; closeCode: number; streak: number };

export interface RecoveryAttemptContext {
  attemptId: string;
  trigger: RecoveryTrigger;
}

export interface RecoveryDecisionObservation<TDiagnostic = undefined> {
  attempt: RecoveryAttemptContext;
  outcome: "success" | "invalid" | "transient" | "attempt_limit";
  decision: "reconnect" | "logout" | "retry_later";
  refreshDiagnostic?: TDiagnostic;
}

/**
 * Classify a rejection from the token-refresh endpoint.
 *  - `invalid`   -> the refresh token itself is no good (401/403). The server
 *                   has cleared the auth cookies; the user must re-auth.
 *  - `transient` -> anything else (network blip, 5xx, timeout). Retry later.
 */
export function classifyRefreshFailure(
  reason: unknown,
): "invalid" | "transient" {
  if (typeof reason === "number") {
    return reason === 401 || reason === 403 ? "invalid" : "transient";
  }
  const text = String(reason ?? "").toLowerCase();
  if (
    text.includes("401") ||
    text.includes("403") ||
    text.includes("unauth") ||
    text.includes("forbidden") ||
    text.includes("invalid_grant") ||
    text.includes("invalid_token") ||
    text.includes("not logged in")
  ) {
    return "invalid";
  }
  return "transient";
}

export interface AuthRecoveryDeps<TDiagnostic = undefined> {
  /** Refresh the access token. Should resolve quickly with an outcome. */
  refresh: (
    attempt?: RecoveryAttemptContext,
  ) => Promise<RefreshOutcome<TDiagnostic>>;
  /** Force the WebSocket to drop + reconnect so subscriptions resubscribe. */
  reconnect: () => void;
  /** Send the (genuinely logged-out) user to login. */
  redirectToLogin: () => void;
  /** Injectable clock for tests. */
  now?: () => number;
  /** Ignore repeat errors that arrive within this window. */
  minIntervalMs?: number;
  /** Max consecutive recovery attempts before backing off. */
  maxAttempts?: number;
  /** After hitting maxAttempts (or a redirect), stay quiet this long. */
  cooldownMs?: number;
  /** A quiet gap longer than this means the last recovery worked -> reset. */
  quietResetMs?: number;
  /** Optional request-local diagnostic context factory. */
  createAttempt?: (trigger: RecoveryTrigger) => RecoveryAttemptContext;
  /** Optional fail-open observer, invoked before reconnect/logout decisions. */
  observeDecision?: (
    observation: RecoveryDecisionObservation<TDiagnostic>,
  ) => void;
}

export interface SubscriptionAuthRecovery {
  /** Call when a subscription frame fails with `unauthorized.user`. */
  handleUnauthorized: (trigger?: RecoveryTrigger) => Promise<void>;
  /** Test-only view of internal counters. */
  _getState: () => {
    attempts: number;
    lastAttemptAt: number;
    cooldownUntil: number;
    inFlight: boolean;
  };
}

export function createSubscriptionAuthRecovery<TDiagnostic = undefined>(
  deps: AuthRecoveryDeps<TDiagnostic>,
): SubscriptionAuthRecovery {
  const now = deps.now ?? (() => Date.now());
  const minIntervalMs = deps.minIntervalMs ?? 5_000;
  const maxAttempts = deps.maxAttempts ?? 5;
  const cooldownMs = deps.cooldownMs ?? 60_000;
  const quietResetMs = deps.quietResetMs ?? 30_000;

  let lastAttemptAt = 0;
  let attempts = 0;
  let inFlight = false;
  let cooldownUntil = 0;

  function observeDecision(
    observation: RecoveryDecisionObservation<TDiagnostic>,
  ) {
    try {
      deps.observeDecision?.(observation);
    } catch {
      // Observability must never change recovery behavior.
    }
  }

  async function handleUnauthorized(
    trigger: RecoveryTrigger = { kind: "subscription_graphql" },
  ): Promise<void> {
    const t = now();

    // One recovery at a time — a burst of failing frames must not fan out into
    // a burst of refreshes.
    if (inFlight) return;
    // Backing off after exhausting attempts (or after a login redirect).
    if (t < cooldownUntil) return;
    // A long quiet period implies the previous recovery succeeded; reset.
    if (lastAttemptAt !== 0 && t - lastAttemptAt > quietResetMs) {
      attempts = 0;
    }
    // Debounce repeated frames from the same failure.
    if (lastAttemptAt !== 0 && t - lastAttemptAt < minIntervalMs) return;

    lastAttemptAt = t;
    attempts += 1;
    const attempt = deps.createAttempt?.(trigger) ?? {
      // eslint-disable-next-line lingui/no-unlocalized-strings -- machine-only fallback
      attemptId: "attempt-0",
      trigger,
    };
    if (attempts > maxAttempts) {
      // Give up forcing reconnects for a while. We deliberately do NOT log the
      // user out here — repeated failures may be a flaky network, and the UI
      // shows a "reconnecting" state (Fix 2) rather than a hard failure.
      attempts = 0;
      cooldownUntil = t + cooldownMs;
      observeDecision({
        attempt,
        outcome: "attempt_limit",
        decision: "retry_later",
      });
      return;
    }

    inFlight = true;
    try {
      const outcome = await deps.refresh(attempt);
      if (outcome.ok) {
        observeDecision({
          attempt,
          outcome: "success",
          decision: "reconnect",
          refreshDiagnostic: outcome.diagnostic,
        });
        deps.reconnect();
        return;
      }
      if (outcome.reason === "invalid") {
        // Definitive: refresh token rejected, cookies cleared server-side.
        attempts = 0;
        cooldownUntil = now() + cooldownMs;
        observeDecision({
          attempt,
          outcome: "invalid",
          decision: "logout",
          refreshDiagnostic: outcome.diagnostic,
        });
        deps.redirectToLogin();
        return;
      }
      // transient: do nothing now; the next frame after minIntervalMs retries.
      observeDecision({
        attempt,
        outcome: "transient",
        decision: "retry_later",
        refreshDiagnostic: outcome.diagnostic,
      });
    } finally {
      inFlight = false;
    }
  }

  return {
    handleUnauthorized,
    _getState: () => ({ attempts, lastAttemptAt, cooldownUntil, inFlight }),
  };
}
